import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Navbar from '../components/Navbar';
import SimplePassportCard from '../components/SimplePassportCard';
import { User, MapPin, Shield, Save, Loader2, Lock, Eye, EyeOff, Phone, Calendar, Heart, ToggleLeft, ToggleRight, ChevronDown, ChevronUp, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const VolunteerProfile = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passport, setPassport] = useState(null);
  const [simplePassport, setSimplePassport] = useState(null);
  const [profile, setProfile] = useState(null);

  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    town: '',
    share_clearance_with_orgs: false,
    share_clearance_level: true,
    share_verification_status: true,
    share_expiration_dates: true,
  });
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    date_of_birth: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
  });
  const [profileEditMode, setProfileEditMode] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);

  const [pwForm, setPwForm] = useState({ current: '', new_pw: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [showPw, setShowPw] = useState({ current: false, new_pw: false, confirm: false });
  const [showPwSection, setShowPwSection] = useState(false);
  const [showSharingDetails, setShowSharingDetails] = useState(false);

  const fetchPassport = useCallback(async () => {
    try {
      const [passportRes, simpleRes, profileRes] = await Promise.all([
        axios.get(`${API}/volunteer/passport`),
        axios.get(`${API}/volunteer/${user?.id}/passport-simple`),
        axios.get(`${API}/auth/profile`),
      ]);
      setPassport(passportRes.data);
      setSimplePassport(simpleRes.data);
      setProfile(profileRes.data);
      setFormData({
        town: passportRes.data.town || '',
        share_clearance_with_orgs: passportRes.data.share_clearance_with_orgs || false,
        share_clearance_level: passportRes.data.share_clearance_level !== false,
        share_verification_status: passportRes.data.share_verification_status !== false,
        share_expiration_dates: passportRes.data.share_expiration_dates !== false,
      });
      setProfileForm({
        name: profileRes.data.name || '',
        phone: profileRes.data.phone || '',
        date_of_birth: profileRes.data.date_of_birth || '',
        emergency_contact_name: profileRes.data.emergency_contact_name || '',
        emergency_contact_phone: profileRes.data.emergency_contact_phone || '',
      });
    } catch (error) {
      toast.error('Failed to load passport');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchPassport();
  }, [fetchPassport]);

  const handleSavePassport = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/volunteer/passport`, formData);
      toast.success('Privacy settings updated');
      setEditMode(false);
      fetchPassport();
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSharing = () => {
    const newValue = !formData.share_clearance_with_orgs;
    setFormData(prev => ({ ...prev, share_clearance_with_orgs: newValue }));
    setEditMode(true);
  };

  const handleGranularToggle = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
    setEditMode(true);
  };

  const handleSaveProfile = async () => {
    if (!profileForm.name?.trim()) {
      return toast.error('Name is required');
    }
    setProfileSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, profileForm);
      toast.success('Profile updated');
      setProfileEditMode(false);
      fetchPassport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!pwForm.current) return toast.error('Enter your current password');
    if (pwForm.new_pw.length < 8) return toast.error('New password must be at least 8 characters');
    if (pwForm.new_pw !== pwForm.confirm) return toast.error('New passwords do not match');
    setPwSaving(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: pwForm.current,
        new_password: pwForm.new_pw,
      });
      toast.success('Password updated successfully');
      setPwForm({ current: '', new_pw: '', confirm: '' });
      setShowPwSection(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/volunteer/passport/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
      toast.success('Passport ready for print');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" data-testid="volunteer-profile">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#8B2E3C] animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF4EA]" data-testid="volunteer-profile">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-4xl font-bold text-[#222] mb-1">My Dashboard</h1>
          <p className="text-sm sm:text-base text-[#666]">Manage your profile, passport, and privacy settings</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Left Column: Passport + Sharing */}
          <div className="lg:col-span-2 space-y-5">
            {/* Passport Card */}
            <SimplePassportCard data={simplePassport} isOwnProfile={true} />

            {/* Print Passport */}
            <button
              onClick={handleDownloadPDF}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border-2 border-[#E9DDCF] rounded-lg text-sm font-medium text-[#444] hover:border-[#8B2E3C] hover:text-[#8B2E3C] transition-colors"
              data-testid="print-passport-btn"
            >
              <FileText className="w-4 h-4" /> Print / Download Passport
            </button>

            {/* Sharing Status Summary */}
            <div className="card" data-testid="sharing-status-card">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5 text-[#8B2E3C]" />
                <h3 className="font-bold text-[#222]">Sharing Status</h3>
              </div>
              <div className={`px-3 py-2 rounded-lg text-sm ${formData.share_clearance_with_orgs ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-600 border border-gray-200'}`} data-testid="sharing-status-indicator">
                {formData.share_clearance_with_orgs ? (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-medium">Passport sharing is ON</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    <span className="font-medium">Passport sharing is OFF</span>
                  </div>
                )}
              </div>
              {formData.share_clearance_with_orgs && (
                <div className="mt-2 text-xs text-[#888]">
                  Partner organizations can see:{' '}
                  {[
                    formData.share_clearance_level && 'clearance level',
                    formData.share_verification_status && 'verification status',
                    formData.share_expiration_dates && 'expiration dates',
                  ].filter(Boolean).join(', ') || 'nothing (enable options below)'}
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Profile Editing + Sharing Controls + Password */}
          <div className="lg:col-span-3 space-y-5">
            {/* Personal Information */}
            <div className="card" data-testid="personal-info-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-[#8B2E3C]" />
                  <h3 className="font-bold text-[#222]">Personal Information</h3>
                </div>
                {!profileEditMode && (
                  <button onClick={() => setProfileEditMode(true)} className="text-sm text-[#8B2E3C] hover:underline font-medium" data-testid="edit-profile-btn">
                    Edit
                  </button>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1">Full Name</label>
                  <input
                    type="text"
                    value={profileForm.name}
                    onChange={(e) => { setProfileForm(prev => ({ ...prev, name: e.target.value })); setProfileEditMode(true); }}
                    className={`input-field ${!profileEditMode ? 'bg-gray-50' : ''}`}
                    readOnly={!profileEditMode}
                    data-testid="profile-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1">Email</label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    className="input-field bg-gray-100"
                    disabled
                    data-testid="profile-email-input"
                  />
                  <p className="text-xs text-[#999] mt-1">Email cannot be changed</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-[#444] mb-1 flex items-center gap-1">
                      <Phone className="w-3.5 h-3.5" /> Phone
                    </label>
                    <input
                      type="tel"
                      value={profileForm.phone}
                      onChange={(e) => { setProfileForm(prev => ({ ...prev, phone: e.target.value })); setProfileEditMode(true); }}
                      placeholder="(555) 123-4567"
                      className={`input-field ${!profileEditMode ? 'bg-gray-50' : ''}`}
                      readOnly={!profileEditMode}
                      data-testid="profile-phone-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#444] mb-1 flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" /> Date of Birth
                    </label>
                    <input
                      type="date"
                      value={profileForm.date_of_birth}
                      onChange={(e) => { setProfileForm(prev => ({ ...prev, date_of_birth: e.target.value })); setProfileEditMode(true); }}
                      className={`input-field ${!profileEditMode ? 'bg-gray-50' : ''}`}
                      readOnly={!profileEditMode}
                      data-testid="profile-dob-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1 flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> Town / City
                  </label>
                  <input
                    type="text"
                    value={formData.town}
                    onChange={(e) => { setFormData(prev => ({ ...prev, town: e.target.value })); setEditMode(true); }}
                    placeholder="Enter your town"
                    className="input-field"
                    data-testid="town-input"
                  />
                </div>
              </div>

              {profileEditMode && (
                <button
                  onClick={handleSaveProfile}
                  disabled={profileSaving}
                  className="btn-primary w-full flex items-center justify-center gap-2 mt-4"
                  data-testid="save-profile-info-button"
                >
                  {profileSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save Profile</>}
                </button>
              )}
            </div>

            {/* Emergency Contact */}
            <div className="card" data-testid="emergency-contact-card">
              <div className="flex items-center gap-2 mb-4">
                <Heart className="w-5 h-5 text-[#8B2E3C]" />
                <h3 className="font-bold text-[#222]">Emergency Contact</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1">Contact Name</label>
                  <input
                    type="text"
                    value={profileForm.emergency_contact_name}
                    onChange={(e) => { setProfileForm(prev => ({ ...prev, emergency_contact_name: e.target.value })); setProfileEditMode(true); }}
                    placeholder="Full name"
                    className="input-field"
                    data-testid="emergency-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#444] mb-1">Contact Phone</label>
                  <input
                    type="tel"
                    value={profileForm.emergency_contact_phone}
                    onChange={(e) => { setProfileForm(prev => ({ ...prev, emergency_contact_phone: e.target.value })); setProfileEditMode(true); }}
                    placeholder="(555) 123-4567"
                    className="input-field"
                    data-testid="emergency-phone-input"
                  />
                </div>
              </div>
              {profileEditMode && (
                <button
                  onClick={handleSaveProfile}
                  disabled={profileSaving}
                  className="btn-primary w-full flex items-center justify-center gap-2 mt-4"
                  data-testid="save-emergency-button"
                >
                  {profileSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save</>}
                </button>
              )}
            </div>

            {/* Passport Sharing Controls */}
            <div className="card" data-testid="sharing-controls-card">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-5 h-5 text-[#8B2E3C]" />
                <h3 className="font-bold text-[#222]">Passport Privacy Settings</h3>
              </div>

              {/* Master Toggle */}
              <div className="flex items-center justify-between p-3 bg-[#FBF4EA] rounded-lg" data-testid="master-sharing-toggle">
                <div className="flex-1 mr-3">
                  <div className="font-medium text-[#222] text-sm">Share my Volunteer Passport with partner organizations</div>
                  <div className="text-xs text-[#888] mt-0.5">
                    When enabled, authorized partner organizations can view your passport information
                  </div>
                </div>
                <button onClick={handleToggleSharing} className="shrink-0" data-testid="toggle-sharing">
                  {formData.share_clearance_with_orgs ? (
                    <ToggleRight className="w-10 h-10 text-[#8B2E3C]" />
                  ) : (
                    <ToggleLeft className="w-10 h-10 text-gray-400" />
                  )}
                </button>
              </div>

              {/* Granular Controls */}
              {formData.share_clearance_with_orgs && (
                <div className="mt-4">
                  <button
                    onClick={() => setShowSharingDetails(!showSharingDetails)}
                    className="flex items-center gap-1 text-sm text-[#8B2E3C] font-medium mb-3 hover:underline"
                    data-testid="toggle-sharing-details"
                  >
                    {showSharingDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    Customize what you share
                  </button>

                  {showSharingDetails && (
                    <div className="space-y-2.5 pl-1" data-testid="granular-sharing-controls">
                      {[
                        { field: 'share_clearance_level', label: 'Clearance / eligibility level', desc: 'Your current clearance level (Community, Youth, Vulnerable Population)' },
                        { field: 'share_verification_status', label: 'Verification status', desc: 'Status of background checks (verified, pending, etc.)' },
                        { field: 'share_expiration_dates', label: 'Expiration dates', desc: 'When your clearance expires' },
                      ].map(item => (
                        <div key={item.field} className="flex items-start justify-between p-2.5 rounded-lg border border-[#E9DDCF] bg-white" data-testid={`sharing-${item.field}`}>
                          <div className="flex-1 mr-2">
                            <div className="text-sm font-medium text-[#333]">{item.label}</div>
                            <div className="text-xs text-[#888] mt-0.5">{item.desc}</div>
                          </div>
                          <button onClick={() => handleGranularToggle(item.field)} className="shrink-0 mt-0.5">
                            {formData[item.field] ? (
                              <ToggleRight className="w-8 h-8 text-[#8B2E3C]" />
                            ) : (
                              <ToggleLeft className="w-8 h-8 text-gray-400" />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <p className="text-xs text-[#999] mt-3">
                Partner organizations will never see criminal records, detailed reports, or internal administrative notes regardless of your sharing settings.
              </p>

              {/* Save Button */}
              {editMode && (
                <button
                  onClick={handleSavePassport}
                  disabled={saving}
                  className="btn-primary w-full flex items-center justify-center gap-2 mt-4"
                  data-testid="save-sharing-button"
                >
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save Privacy Settings</>}
                </button>
              )}
            </div>

            {/* Change Password */}
            <div className="card" data-testid="change-password-section">
              <button
                onClick={() => setShowPwSection(!showPwSection)}
                className="flex items-center justify-between w-full"
                data-testid="toggle-pw-section"
              >
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-[#8B2E3C]" />
                  <h3 className="font-bold text-[#222]">Change Password</h3>
                </div>
                {showPwSection ? <ChevronUp className="w-5 h-5 text-[#888]" /> : <ChevronDown className="w-5 h-5 text-[#888]" />}
              </button>

              {showPwSection && (
                <div className="space-y-3 mt-4">
                  {[
                    { key: 'current', label: 'Current Password', placeholder: 'Enter current password' },
                    { key: 'new_pw', label: 'New Password', placeholder: 'At least 8 characters' },
                    { key: 'confirm', label: 'Confirm New Password', placeholder: 'Re-enter new password' },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-[#444] mb-1">{label}</label>
                      <div className="relative">
                        <input
                          type={showPw[key] ? 'text' : 'password'}
                          value={pwForm[key]}
                          onChange={(e) => setPwForm(prev => ({ ...prev, [key]: e.target.value }))}
                          placeholder={placeholder}
                          className="input-field pr-10"
                          data-testid={`pw-${key}-input`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPw(prev => ({ ...prev, [key]: !prev[key] }))}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888] hover:text-[#444]"
                          tabIndex={-1}
                        >
                          {showPw[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  ))}

                  {pwForm.new_pw && pwForm.confirm && pwForm.new_pw !== pwForm.confirm && (
                    <p className="text-xs text-red-600" data-testid="pw-mismatch-error">Passwords do not match</p>
                  )}
                  {pwForm.new_pw && pwForm.new_pw.length > 0 && pwForm.new_pw.length < 8 && (
                    <p className="text-xs text-red-600" data-testid="pw-length-error">Must be at least 8 characters</p>
                  )}

                  <button
                    onClick={handleChangePassword}
                    disabled={pwSaving || !pwForm.current || !pwForm.new_pw || !pwForm.confirm}
                    className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="change-password-button"
                  >
                    {pwSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Updating...</> : <><Lock className="w-4 h-4" /> Update Password</>}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VolunteerProfile;
